import * as Sentry from "@sentry/react";
import type { AuthUser, SignInOutput } from "aws-amplify/auth";
import { create } from "zustand";
import { authApi } from "../api/auth.api";
import { getCurrentUser as getUserFromApi } from "../api/user.api";
import type { User } from "../schemas/user.schema";

interface AuthState {
  jwt: string | undefined;
  cognitoUser: AuthUser | null;
  currentUser: User | null;
  isInitialized: boolean;

  initialize: () => Promise<void>;
  refreshJwt: () => Promise<void>;
  signIn: (username: string, password: string) => Promise<SignInOutput>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  jwt: undefined,
  cognitoUser: null,
  currentUser: null,
  isInitialized: false,

  initialize: async () => {
    if (get().isInitialized) return;

    try {
      const [jwt, cognitoUser] = await Promise.all([
        authApi.getJwt(),
        authApi.getCurrentUser(),
      ]);

      set({ jwt, cognitoUser, isInitialized: true });

      if (cognitoUser && jwt) {
        try {
          const currentUser = await getUserFromApi();
          set({ currentUser });
          Sentry.setUser({ id: currentUser.authId });
        } catch (error) {
          console.error("Failed to load user data:", error);
        }
      }
    } catch (error) {
      console.error("Auth initialization failed:", error);
      set({ isInitialized: true });
    }
  },

  refreshJwt: async () => {
    const jwt = await authApi.getJwt();
    set({ jwt });
  },

  signIn: async (username, password) => {
    return await authApi.signIn(username, password);
  },

  signOut: async () => {
    await authApi.signOut();
    set({ currentUser: null });
    Sentry.setUser(null);
  },
}));
